import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import Swal from 'sweetalert2';
import { productsService } from '../../services/products';
import { suppliesService } from '../../services/supplies';
import { useAuth } from '../../hooks/useAuth';
import type { PendingProduct } from '../../types/products';
import type { Branch, PaginationInfo, FollowUp } from '../../types/supplies';
import 'react-datepicker/dist/react-datepicker.css';

// Format date as YYYY-MM-DD HH:mm:ss in local time
const formatDateTimeLocal = (date: Date, endOfDay = false) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const time = endOfDay ? '23:59:59' : '00:00:00';
  return `${year}-${month}-${day} ${time}`;
};



// Format date as YYYY-MM-DD in local time
const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to get full photo URL
const getPhotoUrl = (photoPath: string): string => {
  if (photoPath.startsWith('data:') || photoPath.startsWith('http')) {
    return photoPath;
  }
  // Use the API base URL from environment or default
  const apiUrl = import.meta.env.VITE_API_URL;
  const baseUrl = apiUrl.replace('/api', '');
  return `${baseUrl}${photoPath}`;
};

  interface PendingProductFilters {
    branch_id?: number;
    date_from?: string;
    date_to?: string;
    item_code?: string;
    item_name?: string;
    status?: 'pending' | 'blocked' | 'unavailable';
    prod_class?: string;
    stock_status?: 'in stock' | 'out of stock';
    supplier?: string;
    assigned_to?: number;
    viewed?: string[];
    hasFollowUp?: 'yes' | 'no';
    page?: number;
    limit?: number;
  }

const NewProducts: React.FC = () => {
  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());

  // Helper to check if notes are viewed (same logic as Supply Orders)
  const isNotesViewed = (product: PendingProduct) => {
    // is_viewed is a 3-digit string: notes, photos, follow_up (e.g. '100')
    // notes viewed if first digit is '1'
    return product.is_viewed && product.is_viewed.length > 0 && product.is_viewed[0] === '1';
  };

  // Mark notes as viewed and update state
  const markNotesAsViewed = async (productId: number) => {
    try {
      const is_viewed = await suppliesService.updatePendingIsViewed(productId, 'notes');
      setPendingProducts((prev: PendingProductWithViewed[]) => prev.map((p) =>
        p.id === productId
          ? { ...p, notesViewed: true, is_viewed }
          : p
      ));
    } catch (err) {
      alert('Failed to mark Notes as viewed.');
      console.error(err);
    }
  };

  const handleSelectOrder = (productId: number) => {
    setSelectedOrders(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(productId)) {
        newSelected.delete(productId);
      } else {
        newSelected.add(productId);
      }
      return newSelected;
    });
  };

  const isOrderSelected = (orderId: number) => {
    return selectedOrders.has(orderId);
  };


  // Save note logic for notes modal
  const saveNoteModal = async () => {
    if (!notesModal.productId || !notesModal.newNote.trim()) return;
    const newNoteObj = {
      text: notesModal.newNote.trim(),
      timestamp: new Date().toISOString(),
      username: user?.username || 'Unknown'
    };
    const updatedNotesArr = [...notesModal.notes, newNoteObj];
    // Mark notes as viewed when saving a note
    await markNotesAsViewed(notesModal.productId);
    // Always send notes as a JSON array of note objects
    await productsService.updatePendingProductNotes(notesModal.productId, JSON.stringify(updatedNotesArr));
    setNotesModal({ show: false, productId: null, notes: [], newNote: '', isAdding: false });
    await loadPendingProducts();
    Swal.fire({
      icon: 'success',
      title: 'Note Added',
      text: 'Note has been added successfully.',
      timer: 1500,
      showConfirmButton: false
    });
  };
  // Notes modal state for add/view
  type NoteObj = {
    text: string;
    timestamp: string;
    username?: string;
  };
  const [notesModal, setNotesModal] = useState<{
    show: boolean;
    productId: number | null;
    notes: NoteObj[];
    newNote: string;
    isAdding: boolean;
  }>({ show: false, productId: null, notes: [], newNote: '', isAdding: false });
  // Filter collapse state for toggle arrow
  const [filtersCollapsed, setFiltersCollapsed] = React.useState(false);
  // Alert visibility state
  const [showAlert, setShowAlert] = useState(true);
  // View filter logic (copied from SupplyOrders)
  type ViewFilterKey =
    | 'allViewed'
    | 'photosViewed'
    | 'notesViewed'
    | 'followUpViewed'
    | 'allUnViewed'
    | 'photosUnViewed'
    | 'notesUnViewed'
    | 'followUpUnViewed';

  type ViewFilter = {
    [K in ViewFilterKey]: boolean;
  };

  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const [pendingViewFilter, setPendingViewFilter] = useState<ViewFilter>({
    allViewed: false,
    photosViewed: false,
    notesViewed: false,
    followUpViewed: false,
    allUnViewed: false,
    photosUnViewed: false,
    notesUnViewed: false,
    followUpUnViewed: false
  });

  // Supplier editing state and logic
  const [editingSupplier, setEditingSupplier] = useState<{ [key: number]: boolean }>({});
  const [tempSupplier, setTempSupplier] = useState<{ [key: number]: string }>({});

  // Supplier tab (choose/add new), supplier list, and search term states
  const [supplierTab, setSupplierTab] = useState<{ [key: number]: 'existing' | 'new' }>({});
  const [existingSuppliers, setExistingSuppliers] = useState<string[]>([]);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    productsService.getProductSuppliers()
      .then(suppliers => setExistingSuppliers(suppliers || []))
      .catch(err => {
        console.error('Error loading suppliers:', err);
        setExistingSuppliers([]);
      });
  }, []);

  function startEditingSupplier(productId: number, currentSupplier?: string) {
    setEditingSupplier(prev => ({ ...prev, [productId]: true }));
    setTempSupplier(prev => ({ ...prev, [productId]: currentSupplier || '' }));
    setSupplierTab(prev => ({ ...prev, [productId]: 'existing' }));
    setSupplierSearchTerm(prev => ({ ...prev, [productId]: '' }));
  }

  async function saveSupplier(productId: number) {
    try {
      const supplierName = tempSupplier[productId]?.trim() || '';
      await productsService.assignPendingProductSupplier(productId, supplierName);
      setPendingProducts(prev => prev.map(product =>
        product.id === productId
          ? { ...product, supplier: supplierName }
          : product
      ));
      setEditingSupplier(prev => ({ ...prev, [productId]: false }));
      setTempSupplier(prev => {
        const newTemp = { ...prev };
        delete newTemp[productId];
        return newTemp;
      });
    } catch (error) {
      console.error('Error updating supplier:', error);
      alert('Failed to update supplier');
    }
  }

  function cancelEditingSupplier(productId: number) {
    setEditingSupplier(prev => ({ ...prev, [productId]: false }));
    setTempSupplier(prev => {
      const newTemp = { ...prev };
      delete newTemp[productId];
      return newTemp;
    });
    setSupplierSearchTerm(prev => {
      const newSearch = { ...prev };
      delete newSearch[productId];
      return newSearch;
    });
  }

  // Label for dropdown button
  const viewFilterLabel = pendingViewFilter.allViewed
    ? 'All Viewed'
    : pendingViewFilter.allUnViewed
    ? 'All UnViewed'
    : [
        pendingViewFilter.photosViewed && 'Photos Viewed',
        pendingViewFilter.notesViewed && 'Notes Viewed',
        pendingViewFilter.followUpViewed && 'Follow Up Viewed',
        pendingViewFilter.photosUnViewed && 'Photos UnViewed',
        pendingViewFilter.notesUnViewed && 'Notes UnViewed',
        pendingViewFilter.followUpUnViewed && 'Follow Up UnViewed'
      ].filter(Boolean).join(', ') || 'View';

  // Handle logic for radio/checkbox exclusivity
  // When view filter changes, update filters and reload products
  // Do not apply filter or reload products until Refresh is clicked
  // Only update pendingViewFilter state here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // useEffect intentionally left empty for pendingViewFilter
  const handleViewFilterChange = (key: ViewFilterKey, value: boolean) => {
    // Only update pendingViewFilter, do not apply filter or reload until Refresh is clicked
    const oppositeMap: Record<ViewFilterKey, ViewFilterKey> = {
      photosViewed: 'photosUnViewed',
      photosUnViewed: 'photosViewed',
      notesViewed: 'notesUnViewed',
      notesUnViewed: 'notesViewed',
      followUpViewed: 'followUpUnViewed',
      followUpUnViewed: 'followUpViewed',
      allViewed: 'allUnViewed',
      allUnViewed: 'allViewed'
    };
    if (key === 'allViewed') {
      setPendingViewFilter({
        allViewed: value,
        photosViewed: false,
        notesViewed: false,
        followUpViewed: false,
        allUnViewed: false,
        photosUnViewed: false,
        notesUnViewed: false,
        followUpUnViewed: false
      });
    } else if (key === 'allUnViewed') {
      setPendingViewFilter({
        allViewed: false,
        photosViewed: false,
        notesViewed: false,
        followUpViewed: false,
        allUnViewed: value,
        photosUnViewed: false,
        notesUnViewed: false,
        followUpUnViewed: false
      });
    } else if (oppositeMap[key]) {
      setPendingViewFilter((prev) => ({
        ...prev,
        [key]: value,
        [oppositeMap[key]]: value ? false : prev[oppositeMap[key]],
        allViewed: false,
        allUnViewed: false
      }));
    } else {
      setPendingViewFilter((prev) => ({
        ...prev,
        [key]: value,
        allViewed: false,
        allUnViewed: false
      }));
    }
    // Do NOT update backend filters or reload here. Only update pendingViewFilter.
  };
  // Inline status editing state
  const [editingStatus, setEditingStatus] = useState<{ [key: number]: boolean }>({});
  const [tempStatus, setTempStatus] = useState<{ [key: number]: string }>({});
  // Store dropdown position for portal rendering
  // const [dropdownPosition, setDropdownPosition] = useState<{left: number, top: number} | null>(null);
  // Handle status change for a pending product
  // State to track which product's dropdown is open
  const [openDropdownId] = useState<number | null>(null);
  const handleChangeStatus = async (
    productId: number,
    status: 'pending' | 'blocked' | 'unavailable'
  ) => {
    try {
        await productsService.updatePendingProductStatus(productId, status);
        setPendingProducts(prev => prev.map(product =>
          product.id === productId ? { ...product, status } : product
        ));
        Swal.fire({
          icon: 'success',
          title: 'Status Updated',
          text: `Status changed to ${status.charAt(0).toUpperCase() + status.slice(1)}.`,
          confirmButtonColor: '#ff6b35',
        });
    } catch (error) {
      console.error('Error updating status:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update status',
        confirmButtonColor: '#ff6b35',
      });
    }
  };
  const { user } = useAuth();
  // Extend PendingProduct locally to include viewed properties for UI state
  type PendingProductWithViewed = PendingProduct & {
  photosViewed?: boolean;
  followUpViewed?: boolean;
  notesViewed?: boolean;
  is_viewed?: string;
  supplier?: string;
  };
  const [pendingProducts, setPendingProducts] = useState<PendingProductWithViewed[]>([]);
  // Notes editing state
  const [editingNotes, setEditingNotes] = useState<{ [key: number]: boolean }>({});
  const [tempNotes, setTempNotes] = useState<{ [key: number]: string }>({});

  // Save notes for a pending product
  const saveNotes = async (productId: number) => {
    try {
      const notes = tempNotes[productId]?.trim() || '';
      await productsService.updatePendingProductNotes(productId, notes);
      setPendingProducts(prev => prev.map(product =>
        product.id === productId
          ? { ...product, notes }
          : product
      ));
      setEditingNotes(prev => ({ ...prev, [productId]: false }));
      setTempNotes(prev => {
        const newTemp = { ...prev };
        delete newTemp[productId];
        return newTemp;
      });
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Failed to update notes');
    }
  };

  const cancelEditingNotes = (productId: number) => {
    setEditingNotes(prev => ({ ...prev, [productId]: false }));
    setTempNotes(prev => {
      const newTemp = { ...prev };
      delete newTemp[productId];
      return newTemp;
    });
  };
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);

  // Check if user can assign item codes (Purchase roles and Super admin)
  const canAssignItemCode = () => {
    if (!user) return false;
    // Super admin, Purchase admin, Purchase supervisor, and Purchase agent can assign item codes
    return user.role === 'super admin' ||
           user.role === 'Purchase admin' || 
           user.role === 'Purchase supervisor' || 
           user.role === 'Purchase agent';
  };
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 25
  });

  // Sorting state to match SupplyOrders behavior
  const [sortState, setSortState] = useState<{ field?: string; order?: 'asc' | 'desc' | undefined }>({});

  const handleHeaderSort = (field: string) => {
    setSortState(prev => {
      if (prev.field !== field) return { field, order: 'desc' };
      if (prev.order === 'desc') return { field, order: 'asc' };
      return {};
    });
  };

  const renderSortIcon = (field: string) => {
    if (sortState.field !== field) return <i className="bi bi-arrow-repeat text-muted ms-1" />;
    if (sortState.order === 'desc') return <i className="bi bi-arrow-down-short ms-1" />;
    if (sortState.order === 'asc') return <i className="bi bi-arrow-up-short ms-1" />;
    return <i className="bi bi-arrow-repeat text-muted ms-1" />;
  };

  const applySorting = (rows: PendingProductWithViewed[]) => {
    if (!sortState.field) return rows;
    const field = sortState.field;
    const orderMultiplier = sortState.order === 'asc' ? 1 : -1;
    const getVal = (row: PendingProductWithViewed) => {
      switch (field) {
        case 'id': return row.id ?? 0;
        case 'branch': return (row.branch && row.branch.branch) ? row.branch.branch.toString().toLowerCase() : '';
        case 'created_by': return (row.created_by || '').toString().toLowerCase();
        case 'call_center': return (row.call_center || '').toString();
        case 'order_date': return new Date(row.order_date || 0).getTime();
        case 'item_code': return (row.item_code || '').toString().toLowerCase();
        case 'item_name': return (row.item_name || '').toString().toLowerCase();
        case 'quantity': return Number(row.quantity) || 0;
        case 'item_price': return Number(row.item_price) || 0;
        case 'total_price': return Number(row.total_price) || 0;
        case 'supplier': return (row.supplier || '').toString().toLowerCase();
        case 'prod_class': return (row.prod_class || '').toString().toLowerCase();
        case 'last_followup_name': return (row.follow_ups && row.follow_ups.length) ? (row.follow_ups[row.follow_ups.length - 1].user_name || '').toString().toLowerCase() : '';
        case 'last_followup_date': return (row.follow_ups && row.follow_ups.length) ? new Date(row.follow_ups[row.follow_ups.length - 1].created_at).getTime() : 0;
        case 'notes_count': return row.notes ? row.notes.split('\n---\n').length : 0;
        case 'photos_count': return row.photos ? row.photos.length : 0;
        case 'followups_count': return row.follow_ups ? row.follow_ups.length : 0;
        case 'assigned_to_name': return (row.assign_to_name || '').toString().toLowerCase();
        case 'status': return (row.status || '').toString().toLowerCase();
        case 'created_at': return new Date(row.created_at || 0).getTime();
        default: return '';
      }
    };

    const sorted = [...rows].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * orderMultiplier;
      if (va < vb) return -1 * orderMultiplier;
      if (va > vb) return 1 * orderMultiplier;
      return 0;
    });
    return sorted;
  };

  const getFilteredProducts = () => {
    // For now only sorting is applied here; filtering is handled elsewhere via `filters` and server
    return applySorting(pendingProducts);
  };

  const [filters, setFilters] = useState<PendingProductFilters>({
  page: 1,
  limit: 5
  });
  const [productClasses, setProductClasses] = useState<string[]>([]);

  const [editingItemCode, setEditingItemCode] = useState<{ [key: number]: boolean }>({});
  const [tempItemCodes, setTempItemCodes] = useState<{ [key: number]: string }>({});
  
  const [photoModal, setPhotoModal] = useState<{ 
    show: boolean; 
    photos: string[]; 
    currentIndex: number;
  }>({
    show: false,
    photos: [],
    currentIndex: 0
  });
  
  const [followUpModal, setFollowUpModal] = useState<{
    show: boolean;
    productId: number | null;
    followUps: FollowUp[];
    isAdding: boolean;
    newNotes: string;
    newPhotos: string[];
  }>({
    show: false,
    productId: null,
    followUps: [],
    isAdding: false,
    newNotes: '',
    newPhotos: []
  });

  // Assignment state and logic
  const [editingAssignment, setEditingAssignment] = useState<{ [key: number]: boolean }>({});
  const [tempAssignments, setTempAssignments] = useState<{ [key: number]: number | null }>({});
  const [purchaseUsers, setPurchaseUsers] = useState<any[]>([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Load product classes for filter
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const classes = await productsService.getProductClasses();
        setProductClasses(classes);
      } catch (error) {
        console.error('Error loading product classes:', error);
      }
    };
    fetchClasses();
  }, []);

  // Only load products on initial mount or when Refresh is clicked
  // On mount, load products with default filters (no view filter applied)
  // Reload products when filters.page or filters.limit changes
  useEffect(() => {
    loadPendingProducts();
  }, [filters.page, filters.limit]);


  // Only apply view filter and reload products when Refresh is clicked
  const refreshProducts = () => {
    // Follow the exact same pattern as SupplyOrders
    let viewedFilter: string[] | undefined = undefined;
    if (pendingViewFilter.allViewed) viewedFilter = ['111'];
    else if (pendingViewFilter.photosViewed) viewedFilter = ['010'];
    else if (pendingViewFilter.notesViewed) viewedFilter = ['100'];
    else if (pendingViewFilter.followUpViewed) viewedFilter = ['001'];
    else if (pendingViewFilter.allUnViewed) viewedFilter = ['000'];
    else if (pendingViewFilter.photosUnViewed) viewedFilter = ['000','001','100'];
    else if (pendingViewFilter.notesUnViewed) viewedFilter = ['000','001','010'];
    else if (pendingViewFilter.followUpUnViewed) viewedFilter = ['000','010','100'];

    // Apply view filter to the filters and call API directly like SupplyOrders
    const newFilters = {
      ...filters,
      page: 1,
      viewed: viewedFilter
    };
    setFilters(newFilters);
    
    // Fetch using the same server-side strategy as SupplyOrders (send hasFollowUp to backend,
    // and if server returns 0 rows, refetch without hasFollowUp and apply client-side filter)
    (async () => {
      try {
        setLoading(true);
        const backendFilters: any = { ...newFilters };
        const first = await productsService.getPendingProducts(backendFilters as any);
        let firstResponse = first;
        if (backendFilters.hasFollowUp && (!first.pendingProducts || first.pendingProducts.length === 0)) {
          const fallbackFilters = { ...backendFilters };
          delete fallbackFilters.hasFollowUp;
          const fallbackFirst = await productsService.getPendingProducts(fallbackFilters as any);
          firstResponse = fallbackFirst;
        }

        const normalize = (products: any[]) => products.map((product: any) => {
          const is_viewed = (product.is_viewed == null || product.is_viewed === '') ? '111' : product.is_viewed;
          return {
            ...product,
            is_viewed,
            item_price: typeof product.item_price === 'number' ? product.item_price : parseFloat(product.item_price as string) || 0,
            total_price: typeof product.total_price === 'number' ? product.total_price : parseFloat(product.total_price as string) || 0,
            quantity: typeof product.quantity === 'number' ? product.quantity : parseInt(product.quantity as string) || 0,
            notesViewed: is_viewed[0] === '1',
            photosViewed: is_viewed[1] === '1',
            followUpViewed: is_viewed[2] === '1'
          } as PendingProductWithViewed;
        });

        const collected: any[] = [];

        const desired = newFilters.limit || 5;
        const matchHasFollowUp = (item: any) => {
          if (!backendFilters.hasFollowUp) return true;
          if (backendFilters.hasFollowUp === 'yes') return item.follow_ups && item.follow_ups.length > 0;
          if (backendFilters.hasFollowUp === 'no') return !item.follow_ups || item.follow_ups.length === 0;
          return true;
        };

        // Add matching items from firstResponse
        (firstResponse.pendingProducts || []).forEach((p: any) => { if (matchHasFollowUp(p)) collected.push(p); });

        let currentPage = firstResponse.pagination?.currentPage || 1;
        const totalPages = firstResponse.pagination?.totalPages || 1;
        while (collected.length < desired && currentPage < totalPages) {
          currentPage += 1;
          const nextFilters = { ...backendFilters, page: currentPage };
          const nextResp = await productsService.getPendingProducts(nextFilters as any);
          if (!nextResp.pendingProducts || nextResp.pendingProducts.length === 0) break;
          (nextResp.pendingProducts || []).forEach((p: any) => { if (matchHasFollowUp(p)) collected.push(p); });
        }

        let normalizedProducts: PendingProductWithViewed[] = normalize(collected);

        // (Match SupplyOrders) Do NOT apply client-side hasFollowUp filtering here; rely on backend/fallback pagination

        setPendingProducts(normalizedProducts.slice(0, desired));
        setPagination({
          currentPage: 1,
          totalPages: firstResponse.pagination?.totalPages || 1,
          totalRecords: firstResponse.pagination?.totalRecords || 0,
          recordsPerPage: newFilters.limit || 5
        });
      } catch (error) {
        console.error('Error loading pending products:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load pending products',
          confirmButtonColor: '#ff6b35'
        });
      } finally {
        setLoading(false);
      }
    })();
  };

  useEffect(() => {
    // Load all active users for assignment dropdown for all roles
    const fetchUsers = async () => {
      try {
        const usersResponse = await import('../../services/users').then(m => m.usersService.getUsers({ is_active: true }));
        setPurchaseUsers(usersResponse?.data?.users || []);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    fetchUsers();
  }, [user]);

  const loadInitialData = async () => {
    try {
      const branchesData = await suppliesService.getBranches();
      setBranches(branchesData);
    } catch (error) {
      console.error('Error loading branches:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load initial data',
        confirmButtonColor: '#ff6b35'
      });
    }
  };

  const loadPendingProducts = useCallback(async () => {
    try {
      setLoading(true);
      const backendFilters: any = { ...filters };
      const first = await productsService.getPendingProducts(backendFilters as any);
      let firstResponse = first;
      if (backendFilters.hasFollowUp && (!first.pendingProducts || first.pendingProducts.length === 0)) {
        const fallbackFilters = { ...backendFilters };
        delete fallbackFilters.hasFollowUp;
        const fallbackFirst = await productsService.getPendingProducts(fallbackFilters as any);
        firstResponse = fallbackFirst;
      }

      const normalize = (products: any[]) => products.map((product: any) => {
        const is_viewed = (product.is_viewed == null || product.is_viewed === '') ? '111' : product.is_viewed;
        return {
          ...product,
          is_viewed,
          item_price: typeof product.item_price === 'number' ? product.item_price : parseFloat(product.item_price as string) || 0,
          total_price: typeof product.total_price === 'number' ? product.total_price : parseFloat(product.total_price as string) || 0,
          quantity: typeof product.quantity === 'number' ? product.quantity : parseInt(product.quantity as string) || 0,
          notesViewed: is_viewed[0] === '1',
          photosViewed: is_viewed[1] === '1',
          followUpViewed: is_viewed[2] === '1'
        } as PendingProductWithViewed;
      });

      const collected: any[] = [];

      const desired = filters.limit || 5;
      const matchHasFollowUp = (item: any) => {
        if (!backendFilters.hasFollowUp) return true;
        if (backendFilters.hasFollowUp === 'yes') return item.follow_ups && item.follow_ups.length > 0;
        if (backendFilters.hasFollowUp === 'no') return !item.follow_ups || item.follow_ups.length === 0;
        return true;
      };

      // Add matching items from firstResponse
      (firstResponse.pendingProducts || []).forEach((p: any) => { if (matchHasFollowUp(p)) collected.push(p); });

      let currentPage = firstResponse.pagination?.currentPage || 1;
      const totalPages = firstResponse.pagination?.totalPages || 1;
      while (collected.length < desired && currentPage < totalPages) {
        currentPage += 1;
        const nextFilters = { ...backendFilters, page: currentPage };
        const nextResp = await productsService.getPendingProducts(nextFilters as any);
        if (!nextResp.pendingProducts || nextResp.pendingProducts.length === 0) break;
        (nextResp.pendingProducts || []).forEach((p: any) => { if (matchHasFollowUp(p)) collected.push(p); });
      }

      let normalizedProducts: PendingProductWithViewed[] = normalize(collected);

      // (Match SupplyOrders) Do NOT apply client-side hasFollowUp filtering here; rely on backend/fallback pagination

      setPendingProducts(normalizedProducts.slice(0, desired));
      setPagination({
        currentPage: filters.page || 1,
        totalPages: firstResponse.pagination?.totalPages || 1,
        totalRecords: firstResponse.pagination?.totalRecords || 0,
        recordsPerPage: filters.limit || 5
      });
    } catch (error) {
      console.error('Error loading pending products:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load pending products',
        confirmButtonColor: '#ff6b35'
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleFilterChange = (key: keyof PendingProductFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
    // Do NOT reload products here
  };


  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: pagination.recordsPerPage
    });
    setPagination(prev => ({ ...prev, recordsPerPage: pagination.recordsPerPage, currentPage: 1 }));
  };



  // Export to Excel
  const exportToExcel = async () => {
    try {
      const result = await productsService.getPendingProducts({ ...filters, page: undefined, limit: undefined });
      const exportData = result.pendingProducts.map(product => ({
        'ID': product.id,
        'BRANCH': product.branch?.branch || '--',
        'CALL CENTER': product.call_center === 1 ? 'Call Center' : product.call_center === 2 ? 'Social Media' : 'Branch',
        'ORDER DATE': formatDate(product.order_date),
        'ITEM CODE': product.item_code,
        'ITEM NAME': product.item_name,
        'QTY': product.quantity,
        'PRICE': product.item_price,
        'TOTAL': product.total_price,
        'STATUS': product.status,
        'CREATED': formatDate(product.created_at)
      }));
      const XLSX = await import('xlsx');
      const { saveAs } = await import('file-saver');
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pending Products');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(data, 'PendingProducts.xlsx');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Export Error',
        text: 'Failed to export to Excel',
        confirmButtonColor: '#ff6b35'
      });
    }
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    // Products will reload due to useEffect above
  };

  const handlePerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 1 }));
    // Products will reload due to useEffect above
  };

  const startEditingItemCode = (productId: number, currentCode: number) => {
    setEditingItemCode(prev => ({ ...prev, [productId]: true }));
    setTempItemCodes(prev => ({ ...prev, [productId]: currentCode.toString() }));
  };

  const saveItemCode = async (productId: number) => {
    const newItemCode = parseInt(tempItemCodes[productId]) || 0;
    if (newItemCode <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Item Code',
        text: 'Please enter a valid item code (greater than 0)',
        confirmButtonColor: '#ff6b35'
      });
      return;
    }

    // Get supplier assigned to this product
    const supplierName = tempSupplier[productId] || '';

    try {
      // Assign supplier first if not already assigned
      if (supplierName) {
        await productsService.assignPendingProductSupplier(productId, supplierName);
      }
      // Assign item code and move to supply orders
      await productsService.assignItemCode(productId, newItemCode);
      // Remove from pending products list
      setPendingProducts(prev => prev.filter(product => product.id !== productId));
      setEditingItemCode(prev => ({ ...prev, [productId]: false }));
      setTempItemCodes(prev => {
        const newTemp = { ...prev };
        delete newTemp[productId];
        return newTemp;
      });
      // Ensure 'viewed' property is present on all products after update
      setPendingProducts(prev => prev.map(product => ({ ...product, photosViewed: product.photosViewed ?? false, followUpViewed: product.followUpViewed ?? false })));

      Swal.fire({
        icon: 'success',
        title: 'Item Code Assigned',
        text: `Item code ${newItemCode} has been assigned successfully and moved to supply orders.`,
        confirmButtonColor: '#ff6b35'
      });

      // Refresh the list to get updated pagination
      loadPendingProducts();
    } catch (error: any) {
      console.error('Error assigning item code:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to assign item code',
        confirmButtonColor: '#ff6b35'
      });
    }
  };

  const cancelEditingItemCode = (productId: number) => {
    setEditingItemCode(prev => ({ ...prev, [productId]: false }));
    setTempItemCodes(prev => {
      const newTemp = { ...prev };
      delete newTemp[productId];
      return newTemp;
    });
  };

  const handleDeleteProduct = async (productId: number) => {
    const result = await Swal.fire({
      title: 'Delete Product',
      text: 'Are you sure you want to delete this pending product? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await productsService.deletePendingProduct(productId);
  setPendingProducts(prev => prev.filter(product => product.id !== productId));
        
        Swal.fire({
          title: 'Deleted',
          text: 'Pending product has been deleted successfully.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });

        // Refresh to update pagination
        loadPendingProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete pending product',
          confirmButtonColor: '#ff6b35'
        });
      }
    }
  };

  const showPhotos = (photos: string[]) => {
    // Convert relative URLs to full URLs
    const fullPhotoUrls = photos.map(photo => getPhotoUrl(photo));
    setPhotoModal({
      show: true,
      photos: fullPhotoUrls,
      currentIndex: 0
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getLastFollowUp = (product: PendingProduct) => {
    if (!product.follow_ups || product.follow_ups.length === 0) {
      return { name: '--', date: '--' };
    }
    
    const lastFollowUp = product.follow_ups[product.follow_ups.length - 1];
    return {
      name: lastFollowUp.user_name,
      date: formatDate(lastFollowUp.created_at)
    };
  };

  const showFollowUps = (productId: number) => {
    const product = pendingProducts.find(p => p.id === productId);
    if (product) {
      // Convert relative photo URLs to full URLs in follow-ups
      const followUpsWithFullUrls = (product.follow_ups || []).map(followUp => ({
        ...followUp,
        photos: (followUp.photos || []).map(photo => getPhotoUrl(photo))
      }));
      
      setFollowUpModal({
        show: true,
        productId,
        followUps: followUpsWithFullUrls,
        isAdding: false,
        newNotes: '',
        newPhotos: []
      });
    }
  };

  const addFollowUp = async () => {
    if (!followUpModal.productId || !followUpModal.newNotes.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Notes Required',
        text: 'Please enter follow-up notes',
        confirmButtonColor: '#ff6b35'
      });
      return;
    }

    try {
      await productsService.addFollowUp(
        followUpModal.productId,
        followUpModal.newNotes,
        followUpModal.newPhotos
      );

      // Reset modal state
      setFollowUpModal(prev => ({
        ...prev,
        isAdding: false,
        newNotes: '',
        newPhotos: []
      }));

      // Refresh data from server to get updated timestamps and follow-ups
      await loadPendingProducts();

      Swal.fire({
        icon: 'success',
        title: 'Follow-up Added',
        text: 'Follow-up has been added successfully',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error adding follow-up:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add follow-up',
        confirmButtonColor: '#ff6b35'
      });
    }
  };

  const PhotoUploadComponent: React.FC<{ onPhotosAdded: (photos: string[]) => void }> = ({ onPhotosAdded }) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const validFiles: File[] = [];
      
      // First, validate all files and collect valid ones
      Array.from(files).forEach(file => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          Swal.fire({
            icon: 'warning',
            title: 'Invalid File Type',
            text: `File "${file.name}" is not a valid image file.`,
            confirmButtonColor: '#ff6b35'
          });
          return;
        }

        // Validate file size (10MB limit)
        if (file.size > 10485760) {
          Swal.fire({
            icon: 'warning',
            title: 'File Too Large',
            text: `File "${file.name}" is larger than 10MB limit.`,
            confirmButtonColor: '#ff6b35'
          });
          return;
        }

        validFiles.push(file);
      });

      if (validFiles.length === 0) {
        return;
      }

      const photosToAdd: string[] = [];
      let filesProcessed = 0;

      // Process valid files
      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          
          if (base64) {
            // Create an image to get dimensions and compress if needed
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              // Set maximum dimensions (compress large images)
              const MAX_WIDTH = 800;
              const MAX_HEIGHT = 600;
              let { width, height } = img;
              
              // Calculate new dimensions
              if (width > height) {
                if (width > MAX_WIDTH) {
                  height = height * (MAX_WIDTH / width);
                  width = MAX_WIDTH;
                }
              } else {
                if (height > MAX_HEIGHT) {
                  width = width * (MAX_HEIGHT / height);
                  height = MAX_HEIGHT;
                }
              }
              
              // Set canvas dimensions and draw compressed image
              canvas.width = width;
              canvas.height = height;
              ctx?.drawImage(img, 0, 0, width, height);
              
              // Get compressed base64 (with reduced quality)
              const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
              photosToAdd.push(compressedBase64);
              
              filesProcessed++;
              
              // When all valid files are processed, call onPhotosAdded
              if (filesProcessed === validFiles.length) {
                onPhotosAdded(photosToAdd);
              }
            };
            img.src = base64;
          } else {
            filesProcessed++;
            
            // Still check if all files are processed even if base64 is empty
            if (filesProcessed === validFiles.length && photosToAdd.length > 0) {
              onPhotosAdded(photosToAdd);
            }
          }
        };
        
        reader.onerror = (error) => {
          console.error('Error reading file:', file.name, error);
          filesProcessed++;
          
          // Still check if all files are processed even on error
          if (filesProcessed === validFiles.length && photosToAdd.length > 0) {
            onPhotosAdded(photosToAdd);
          }
        };
        
        reader.readAsDataURL(file);
      });

      // Reset the input value
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleUploadClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    return (
      <div className="photo-dropzone" onClick={handleUploadClick} >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileInputChange}
        
        />
        <i className="bi bi-cloud-upload"></i>
        <p className="mb-0">Click to select photos</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-orange" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted mt-2">Loading pending products...</p>
      </div>
    );
  }

  function startEditingAssignment(productId: number, currentAssignedTo?: number) {
    setEditingAssignment(prev => ({ ...prev, [productId]: true }));
    setTempAssignments(prev => ({ ...prev, [productId]: currentAssignedTo || null }));
  }

  async function saveAssignment(productId: number) {
    try {
      const assignTo = tempAssignments[productId] === null ? undefined : tempAssignments[productId];
      await productsService.assignPendingProductUser(productId, assignTo!);
      setPendingProducts(prev => prev.map(product =>
        product.id === productId
          ? { ...product, assign_to: assignTo, assign_to_name: assignTo ? purchaseUsers.find(u => u.id === assignTo)?.username : undefined }
          : product
      ));
      setEditingAssignment(prev => ({ ...prev, [productId]: false }));
      setTempAssignments(prev => {
        const newTemp = { ...prev };
        delete newTemp[productId];
        return newTemp;
      });
    } catch (error) {
      console.error('Error updating assignment:', error);
      alert('Failed to update assignment');
    }
  }

  function cancelEditingAssignment(productId: number) {
    setEditingAssignment(prev => ({ ...prev, [productId]: false }));
    setTempAssignments(prev => {
      const newTemp = { ...prev };
      delete newTemp[productId];
      return newTemp;
    });
  }

  return (
    <>
  <div className="supply-orders-container">
    {/* Header */}
    {showAlert && (
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="bi bi-box-seam me-2"></i>
            New Products - Pending Item Code Assignment
          </h5>
        </div>
        <div className="card-body">
            <div className="alert alert-info d-flex justify-content-between align-items-start">
              <div>
                <i className="bi bi-info-circle me-2"></i>
                These are products that were entered with item code "0" and are waiting for proper item code assignment.
                Once assigned, they will be moved to the Supply Orders.
                {!canAssignItemCode() && (
                  <>
                    <br />
                    <strong>Note:</strong> Item code assignment is restricted to Super admin and Purchase department roles only.
                  </>
                )}
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setShowAlert(false)}
              ></button>
            </div>
        </div>
      </div>
    )}

  {/* Filters Toggle Arrow */}
  <div>
      <div className="filter-toggle" >
        <button
          type="button"
          className="btn btn-light btn-sm filter-controls-toggle"
          onClick={() => setFiltersCollapsed((prev) => !prev)}
          aria-label={filtersCollapsed ? 'Expand filters' : 'Collapse filters'}
        >
          <i className={`bi ${filtersCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
        </button>
        <span >Filters</span>
      </div>
      {/* Filters - Supply Orders */}
      {!filtersCollapsed && (
        <div className="filter-controls">
          <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Branch</label>
            <select
              className="form-select form-select-sm"
              value={filters.branch_id || ''}
              onChange={e => handleFilterChange('branch_id', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">All Branches</option>
              {branches.map(branch => (
                <option key={branch.branch_id} value={branch.branch_id}>{branch.branch}</option>
              ))}
            </select>
          </div>
     
          <div className="col-md-1">
            <label className="form-label">Date From</label>
            <DatePicker
              selected={filters.date_from ? new Date(filters.date_from) : null}
              onChange={date => {
                if (!date) return handleFilterChange('date_from', undefined);
                // If date_to is set and same as date_from, set start of day
                if (filters.date_to && formatDateLocal(date) === formatDateLocal(new Date(filters.date_to))) {
                  handleFilterChange('date_from', formatDateTimeLocal(date, false));
                  handleFilterChange('date_to', formatDateTimeLocal(date, true));
                } else {
                  handleFilterChange('date_from', formatDateLocal(date));
                }
              }}
              dateFormat="dd/MM/yyyy"
              className="form-control form-control-sm"
              placeholderText="Select date"
              isClearable
            />
          </div>
          <div className="col-md-1">
            <label className="form-label">Date To</label>
            <DatePicker
              selected={filters.date_to ? new Date(filters.date_to) : null}
              onChange={date => {
                if (!date) return handleFilterChange('date_to', undefined);
                // If date_from is set and same as date_to, set end of day
                if (filters.date_from && formatDateLocal(date) === formatDateLocal(new Date(filters.date_from))) {
                  handleFilterChange('date_from', formatDateTimeLocal(date, false));
                  handleFilterChange('date_to', formatDateTimeLocal(date, true));
                } else {
                  handleFilterChange('date_to', formatDateLocal(date));
                }
              }}
              dateFormat="dd/MM/yyyy"
              className="form-control form-control-sm"
              placeholderText="Select date"
              isClearable
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Item Name</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={filters.item_name || ''}
              onChange={e => handleFilterChange('item_name', e.target.value)}
              placeholder="Search name"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select
              className="form-select form-select-sm"
              value={filters.status || ''}
              onChange={e => handleFilterChange('status', e.target.value || undefined)}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="blocked">Blocked</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Product Class</label>
            <select
              className="form-select form-select-sm"
              value={filters.prod_class || ''}
              onChange={e => handleFilterChange('prod_class', e.target.value || undefined)}
            >
              <option value="">All Classes</option>
              <option value="--">--</option>
              {productClasses.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Assigned To</label>
            <select
              className="form-select form-select-sm"
              value={filters.assigned_to || ''}
              onChange={e => handleFilterChange('assigned_to', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">All Users</option>
              {purchaseUsers.map(user => (
                <option key={user.id} value={user.id}>{user.username}</option>
              ))}
            </select>
          </div>
              <div className="col-md-3">
            <label className="form-label">Assigned Supplier</label>
            <div className="supplier-search-dropdown">
              <input
                type="text"
                className="form-control form-control-sm"
                value={filters.supplier || ''}
                onChange={e => handleFilterChange('supplier', e.target.value)}
                placeholder="Search supplier"
                list="supplier-options"
              />
              <datalist id="supplier-options">
                <option value="">All Suppliers</option>
                {existingSuppliers.map((supplier, idx) => (
                  <option key={idx} value={supplier}>
                    {supplier}
                  </option>
                ))}
              </datalist>
            </div>
          </div>
          <div className="col-md-3">
            <label className="form-label">Per Page</label>
            <select
              className="form-select form-select-sm"
              value={filters.limit || 5}
              onChange={e => handlePerPageChange(parseInt(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={50000}>Show All Rows</option>
            </select>
          </div>
              <div className="col-md-3">
                <label className="form-label">Has Follow Up</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.hasFollowUp || ''}
                  onChange={(e) => handleFilterChange('hasFollowUp', e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="yes">Has Follow Up</option>
                  <option value="no">Has No Follow Up</option>
                </select>
              </div>
          {/* View Filter Dropdown */}
          <div className="col-md-3">
            <label className="form-label">View</label>
            <div className="dropdown position-relative">
              <button
                type="button"
                className="form-select form-select-sm d-flex justify-content-between align-items-center"
                onClick={() => setViewDropdownOpen((open) => !open)}
              >
                {viewFilterLabel}
              </button>
              {viewDropdownOpen && (
                <div className="dropdown-menu show p-2 view-filter-dropdown">
                  <div className="mb-2 fw-bold">Viewed</div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" name="viewedGroup" id="allViewed" checked={pendingViewFilter.allViewed} onChange={() => handleViewFilterChange('allViewed', true)} />
                    <label className="form-check-label" htmlFor="allViewed">All Viewed</label>
                  </div>
                  <div className="ms-3">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="photosViewed" checked={pendingViewFilter.photosViewed} disabled={pendingViewFilter.allViewed} onChange={() => handleViewFilterChange('photosViewed', !pendingViewFilter.photosViewed)} />
                      <label className="form-check-label" htmlFor="photosViewed">Photos Viewed</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="notesViewed" checked={pendingViewFilter.notesViewed} disabled={pendingViewFilter.allViewed} onChange={() => handleViewFilterChange('notesViewed', !pendingViewFilter.notesViewed)} />
                      <label className="form-check-label" htmlFor="notesViewed">Notes Viewed</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="followUpViewed" checked={pendingViewFilter.followUpViewed} disabled={pendingViewFilter.allViewed} onChange={() => handleViewFilterChange('followUpViewed', !pendingViewFilter.followUpViewed)} />
                      <label className="form-check-label" htmlFor="followUpViewed">Follow Up Viewed</label>
                    </div>
                  </div>
                  <div className="mt-2 fw-bold">UnViewed</div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" name="viewedGroup" id="allUnViewed" checked={pendingViewFilter.allUnViewed} onChange={() => handleViewFilterChange('allUnViewed', true)} />
                    <label className="form-check-label" htmlFor="allUnViewed">All UnViewed</label>
                  </div>
                  <div className="ms-3">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="photosUnViewed" checked={pendingViewFilter.photosUnViewed} disabled={pendingViewFilter.allUnViewed} onChange={() => handleViewFilterChange('photosUnViewed', !pendingViewFilter.photosUnViewed)} />
                      <label className="form-check-label" htmlFor="photosUnViewed">Photos UnViewed</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="notesUnViewed" checked={pendingViewFilter.notesUnViewed} disabled={pendingViewFilter.allUnViewed} onChange={() => handleViewFilterChange('notesUnViewed', !pendingViewFilter.notesUnViewed)} />
                      <label className="form-check-label" htmlFor="notesUnViewed">Notes UnViewed</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="followUpUnViewed" checked={pendingViewFilter.followUpUnViewed} disabled={pendingViewFilter.allUnViewed} onChange={() => handleViewFilterChange('followUpUnViewed', !pendingViewFilter.followUpUnViewed)} />
                      <label className="form-check-label" htmlFor="followUpUnViewed">Follow Up UnViewed</label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <button
              type="button"
              className="btn btn-outline-danger btn-sm me-2"
              onClick={clearFilters}
            >
              <i className="bi bi-x-circle me-1"></i>
              Clear Filters
            </button>
            <button
              type="button"
              className="btn btn-outline-success btn-sm me-2"
              onClick={refreshProducts}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
            <button
              type="button"
              className="btn export-excel-btn btn-sm"
              onClick={exportToExcel}
            >
              <i className="bi bi-file-earmark-excel me-1"></i>
              Export Excel
              
            </button>
           </div>
          </div>
        </div>
      )}
      </div>

      {/* Pending Products Table */}
      <div className="data-table-container">
        <div className="table-responsive">
          <table className="table table-striped table-hover supply-orders-table-layout">
            <thead className="sticky-header">
              <tr className="text-truncate">
                <th>Check box</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('id')}>ID {renderSortIcon('id')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('branch')}>BRANCH {renderSortIcon('branch')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('created_by')}>CREATED BY {renderSortIcon('created_by')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('call_center')}>CATEGORY {renderSortIcon('call_center')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('order_date')}>ORDER DATE {renderSortIcon('order_date')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('item_code')}>ITEM CODE {renderSortIcon('item_code')}</th>
                <th className="supply-orders-item-name-header" style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('item_name')}>ITEM NAME {renderSortIcon('item_name')}</th>
                {/* Supplier column only for non-supplies_agent */}
                {user?.role !== 'Supplies agent' && <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('supplier')}>SUPPLIER {renderSortIcon('supplier')}</th>}
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('supplier')}>ASSIGNED SUPPLIER {renderSortIcon('supplier')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('prod_class')}>PRODUCT CLASS {renderSortIcon('prod_class')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('quantity')}>QUANTITY {renderSortIcon('quantity')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('item_price')}>PRICE {renderSortIcon('item_price')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('total_price')}>TOTAL {renderSortIcon('total_price')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('notes_count')}>NOTES {renderSortIcon('notes_count')}</th>
                <th>ASSIGN TO</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('assigned_to_name')}>ASSIGNED TO {renderSortIcon('assigned_to_name')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('photos_count')}>PHOTOS {renderSortIcon('photos_count')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('followups_count')}>FOLLOW UP {renderSortIcon('followups_count')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('last_followup_name')}>LAST FOLLOW UP NAME {renderSortIcon('last_followup_name')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('last_followup_date')}>LAST FOLLOW UP DATE {renderSortIcon('last_followup_date')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('status')}>STATUS {renderSortIcon('status')}</th>
                <th style={{cursor: 'pointer'}} onClick={() => handleHeaderSort('created_at')}>CREATED {renderSortIcon('created_at')}</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {pendingProducts.length === 0 ? (
                <tr>
                  <td colSpan={24} className="text-center py-5">
                    <div className="empty-state">
                      <i className="bi bi-inbox"></i>
                      <h6 className="text-muted mt-2">No pending products found</h6>
                      <p className="text-muted">
                        Pending products will appear here when items are created with item code "0"
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                getFilteredProducts().map((product) => {
                const isSelected = isOrderSelected(product.id);
                  return (
                    <tr 
                        className={`text-truncate ${isSelected ? 'table-row-selected' : ''}`} 
                        key={product.id}
                      >
                        <td>
                          <input 
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectOrder(product.id)}
                          />
                        </td>

                      <td>{product.id}</td>
                      <td className="text-truncate">{product.branch?.branch || '--'}</td>
                      <td>{product.created_by || '--'}</td>
                      <td className="text-truncate">
                        <span className={`badge ${product.call_center === 1 ? 'bg-success' : product.call_center === 2 ? 'bg-info' : 'bg-danger'}`}>
                          {product.call_center === 1 ? 'Call Center' : product.call_center === 2 ? 'Social Media' : 'Branch'}
                        </span>
                      </td>
                      <td>{formatDate(product.order_date)}</td>
                      <td>
                        {canAssignItemCode() && editingItemCode[product.id] ? (
                          <div className="input-group input-group-sm item-code-input-group">
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={tempItemCodes[product.id] || ''}
                              onChange={(e) => setTempItemCodes(prev => ({
                                ...prev,
                                [product.id]: e.target.value
                              }))}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') saveItemCode(product.id);
                                if (e.key === 'Escape') cancelEditingItemCode(product.id);
                              }}
                              placeholder="Enter item code"
                              autoFocus
                            />
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => saveItemCode(product.id)}
                              title="Save item code"
                            >
                              <i className="bi bi-check"></i>
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => cancelEditingItemCode(product.id)}
                              title="Cancel"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        ) : (
                          <span
                            className={`badge ${canAssignItemCode() ? 'bg-warning text-dark new-products-clickable-badge' : 'bg-secondary'}`}
                            onClick={canAssignItemCode() ? () => startEditingItemCode(product.id, product.item_code) : undefined}
                            title={canAssignItemCode() ? "Click to assign item code" : "Item code assignment not available for your role"}
                          >
                            {product.item_code || '0'} {canAssignItemCode() ? '(Click to assign)' : '(Pending assignment)'}
                          </span>
                        )}
                      </td>
                      <td className="text-truncate supply-orders-item-name-cell"  title={product.item_name}>
                        {product.item_name}
                      </td>
                                          {/* Supplier column only for non-supplies_agent */}
                      {user?.role !== 'Supplies agent' && (
                        <td className="text-truncate">
                          {editingSupplier[product.id] ? (
                            supplierTab[product.id] === 'existing' ? (
                              <div className="supplier-selection">
                                <div className="d-flex">
                                  <div className="me-1 supplier-selection-container">
                                    <input
                                      type="text"
                                      className="form-control form-control-sm mb-1 supplier-search-input"
                                      placeholder="Search..."
                                      value={supplierSearchTerm[product.id] || ''}
                                      onChange={e => setSupplierSearchTerm(prev => ({ ...prev, [product.id]: e.target.value }))}
                                    />
                                    <select
                                      className="form-select form-select-sm supplier-select"
                                      value={tempSupplier[product.id] || ''}
                                      onChange={e => setTempSupplier(prev => ({ ...prev, [product.id]: e.target.value }))}
                                    >
                                      <option value="">Select</option>
                                      {existingSuppliers
                                        .filter(supplier => 
                                          !supplierSearchTerm[product.id] || 
                                          supplier.toLowerCase().includes(supplierSearchTerm[product.id]?.toLowerCase() || '')
                                        )
                                        .map((supplier, idx) => (
                                          <option key={idx} value={supplier}>
                                            {supplier}
                                          </option>
                                        ))
                                      }
                                    </select>
                                    <div className="d-flex mt-1">     
                                      <button
                                        className="btn btn-success btn-sm me-2 px-4 py-0 supplier-action-btn"
                                        onClick={() => saveSupplier(product.id)}
                                        title="Assign supplier"
                                      >
                                        <i className="bi bi-check"></i>
                                      </button>
                                      <button
                                        className="btn btn-secondary btn-sm me-2 px-4 py-0 supplier-action-btn"
                                        onClick={() => cancelEditingSupplier(product.id)}
                                        title="Cancel"
                                      >
                                        <i className="bi bi-x"></i>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="input-group input-group-sm">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={tempSupplier[product.id] || ''}
                                  onChange={e => setTempSupplier(prev => ({ ...prev, [product.id]: e.target.value }))}
                                  placeholder="Enter supplier name"
                                />
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => saveSupplier(product.id)}
                                  title="Save supplier"
                                >
                                  <i className="bi bi-check"></i>
                                </button>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => cancelEditingSupplier(product.id)}
                                  title="Cancel"
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </div>
                            )
                          ) : (
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                  startEditingSupplier(product.id, product.supplier);
                                  setSupplierTab(prev => ({ ...prev, [product.id]: 'existing' }));
                                }}
                                title="Choose existing supplier"
                              >
                                <i className="bi bi-list"></i> Choose
                              </button>
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={() => {
                                  startEditingSupplier(product.id, product.supplier);
                                  setSupplierTab(prev => ({ ...prev, [product.id]: 'new' }));
                                }}
                                title="Add new supplier"
                              >
                                <i className="bi bi-plus"></i> Add New
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                      {/* Assigned Supplier column */}
                      <td className="text-truncate" >
                        {product.supplier ? (
                          <span className="badge bg-info text-dark">{product.supplier}</span>
                        ) : (
                          <span className="text-muted">--</span>
                        )}
                      </td>
                      <td className="text-truncate" title={product.prod_class}>
                        {product.prod_class || '--'}
                      </td>
                      <td className="text-truncate">{product.quantity}</td>
                      <td>${product.item_price.toFixed(2)}</td>
                      <td>${product.total_price.toFixed(2)}</td>
                      
                      {/* Notes column after Total - Supply Orders logic */}
                      <td className="text-truncate">
                        <div className="d-flex gap-1 notes-actions">
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => setNotesModal({ show: true, productId: product.id, notes: product.notes ? product.notes.split('\n---\n').map(note => { try { const obj = JSON.parse(note); if (obj && obj.text && obj.timestamp) return obj; } catch { } return { text: note, timestamp: 'Unknown' }; }) : [], newNote: '', isAdding: true })}
                          >
                            <i className="bi bi-plus"></i> Add
                          </button>
                          <button
                            className={`btn btn-sm ${isNotesViewed(product) ? 'btn-primary' : 'btn-danger'}`}
                            disabled={!(product.notes && product.notes.length > 0)}
                            onClick={async () => {
                              setNotesModal({ show: true, productId: product.id, notes: product.notes ? product.notes.split('\n---\n').map(note => { try { const obj = JSON.parse(note); if (obj && obj.text && obj.timestamp) return obj; } catch { } return { text: note, timestamp: 'Unknown' }; }) : [], newNote: '', isAdding: false });
                              if (!isNotesViewed(product)) {
                                await markNotesAsViewed(product.id);
                              }
                            }}
                          >
                            <i className="bi bi-eye"></i> {isNotesViewed(product) ? 'Viewed' : 'View'} ({product.notes ? product.notes.split('\n---\n').length : 0})
                          </button>
                        </div>
                      </td>

                      {/* Assign To column */}
                      <td >
                        {editingAssignment[product.id] ? (
                          <div className="input-group input-group-sm assignment-input-group">
                            <select
                              className="form-select form-select-sm assignment-select"
                              value={tempAssignments[product.id] || ''}
                              onChange={e => setTempAssignments(prev => ({ ...prev, [product.id]: e.target.value ? parseInt(e.target.value) : null }))}
                            >
                              <option value="">Select user</option>
                              {purchaseUsers.map(user => (
                                <option key={user.id} value={user.id}>{user.username}</option>
                              ))}
                            </select>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => saveAssignment(product.id)}
                              title="Assign"
                            >
                              Assign
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => cancelEditingAssignment(product.id)}
                              title="Cancel"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline-primary btn-sm assign-to-btn"
                            onClick={() => startEditingAssignment(product.id, product.assign_to)}
                            title="Assign to user"
                          >
                            Assign To
                          </button>
                        )}
                      </td>
                      {/* Assigned To column */}
                      <td className="text-truncate" title={(() => {
                        const user = purchaseUsers.find(u => u.id === product.assign_to);
                        return user ? user.username : '--';
                      })()}>
                        {(() => {
                          const user = purchaseUsers.find(u => u.id === product.assign_to);
                          return user ? user.username : '--';
                        })()}
                      </td>
                      <td>
                        {product.photos && product.photos.length > 0 ? (
                          <button
                            className={`btn btn-sm ${product.photosViewed ? 'btn-primary' : 'btn-danger'}`}
                            onClick={() => {
                              showPhotos(product.photos!);
                              if (!product.photosViewed) {
                                suppliesService.updatePendingIsViewed(product.id, 'photos')
                                  .then((is_viewed) => {
                                    setPendingProducts((prev: PendingProductWithViewed[]) => prev.map((p) =>
                                      p.id === product.id
                                        ? { ...p, photosViewed: true, is_viewed }
                                        : p
                                    ));
                                  })
                                  .catch((err) => {
                                    alert('Failed to mark Photos as viewed.');
                                    console.error(err);
                                  });
                              }
                            }}
                          >
                            <i className="bi bi-eye"></i> {product.photosViewed ? 'Viewed' : 'View'} ({product.photos.length})
                          </button>
                        ) : (
                          <span className="text-muted">No photos</span>
                        )}
                      </td>
                      <td>
                        <div className="follow-up-actions">
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => setFollowUpModal(prev => ({
                              ...prev,
                              show: true,
                              productId: product.id,
                              followUps: product.follow_ups || [],
                              isAdding: true
                            }))}
                          >
                            <i className="bi bi-plus"></i> Add
                          </button>
                          {product.follow_ups && product.follow_ups.length > 0 && (
                            <button
                              className={`btn btn-sm ${product.followUpViewed ? 'btn-primary' : 'btn-danger'}`}
                              onClick={() => {
                                showFollowUps(product.id);
                                if (!product.followUpViewed) {
                                  suppliesService.updatePendingIsViewed(product.id, 'follow_up')
                                    .then((is_viewed) => {
                                      setPendingProducts((prev: PendingProductWithViewed[]) => prev.map((p) =>
                                        p.id === product.id
                                          ? { ...p, followUpViewed: true, is_viewed }
                                          : p
                                      ));
                                    })
                                    .catch((err) => {
                                      alert('Failed to mark Follow Up as viewed.');
                                      console.error(err);
                                    });
                                }
                              }}
                            >
                              <i className="bi bi-eye"></i> {product.followUpViewed ? 'Viewed' : 'View'} ({product.follow_ups.length})
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="text-truncate" title={getLastFollowUp(product).name}>{getLastFollowUp(product).name}</td>
                      <td className="text-truncate" title={getLastFollowUp(product).date}>{getLastFollowUp(product).date}</td>

                      <td>
                        {product.status === 'pending' && (
                          <span className="badge bg-warning text-dark">PENDING</span>
                        )}
                        {product.status === 'blocked' && (
                          <span className="badge bg-danger text-light">BLOCKED</span>
                        )}
                        {product.status === 'unavailable' && (
                          <span className="badge bg-orange text-light">UNAVAILABLE</span>
                        )}
                      </td>
                      <td>{formatDate(product.created_at)}</td>
                      <td>
                        <div className="btn-group-sm d-flex align-items-center position-relative actions-container">
                          {/* Status Change Dropdown Button */}
                          <div className={`dropdown me-1 position-relative actions-dropdown${openDropdownId === product.id ? ' show' : ''}`}>
                            {editingStatus[product.id] ? (
                              <div className="input-group input-group-sm status-edit-input-group">
                                <select
                                  className={`form-select form-select-sm status-select ${tempStatus[product.id] || product.status || 'default'}`}
                                  value={tempStatus[product.id] || product.status}
                                  onChange={e => setTempStatus(prev => ({ ...prev, [product.id]: e.target.value }))}
                                >
                                  <option value="pending" className="pending">Pending</option>
                                  <option value="blocked" className="blocked">Blocked</option>
                                  <option value="unavailable" className="unavailable">Unavailable</option>
                                </select>
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => {
                                    handleChangeStatus(product.id, tempStatus[product.id] as 'pending' | 'blocked' | 'unavailable');
                                    setEditingStatus(prev => ({ ...prev, [product.id]: false }));
                                  }}
                                  title="Update status"
                                >
                                  <i className="bi bi-check"></i>
                                </button>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setEditingStatus(prev => ({ ...prev, [product.id]: false }))}
                                  title="Cancel"
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn btn-sm status-change-btn"
                                onClick={() => {
                                  setEditingStatus(prev => ({ ...prev, [product.id]: true }));
                                  setTempStatus(prev => ({ ...prev, [product.id]: product.status }));
                                }}
                                title="Change Status"
                              >
                                <i className="bi bi-arrow-repeat"></i>
                              </button>
                            )}
                          </div>
                          <button
                            className={`btn btn-sm btn-danger ${canAssignItemCode() ? 'ms-1' : ''}`}
                            onClick={() => handleDeleteProduct(product.id)}
                            title="Delete pending product"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
 {/* Notes Modal */}
  {notesModal.show && (
    <>
      <div className="modal-backdrop show"  onClick={() => setNotesModal({ show: false, productId: null, notes: [], newNote: '', isAdding: false })}></div>
      <div className="modal show d-block" tabIndex={-1}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-journal-text me-2 text-orange"></i> Notes ({notesModal.notes.length})</h5>
            <button type="button" className="btn-close" onClick={() => setNotesModal({ show: false, productId: null, notes: [], newNote: '', isAdding: false })}></button>
          </div>
          <div className="modal-body">
            {notesModal.isAdding ? (
              <>
                <textarea
                  className="form-control mb-2"
                  rows={3}
                  value={notesModal.newNote}
                  onChange={e => setNotesModal(prev => ({ ...prev, newNote: e.target.value }))}
                  placeholder="Enter note..."
                  autoFocus
                />
                <div className="d-flex gap-2">
                  <button className="btn btn-success" onClick={saveNoteModal}><i className="bi bi-check me-1"></i>Save</button>
                  <button className="btn btn-secondary" onClick={() => setNotesModal({ show: false, productId: null, notes: [], newNote: '', isAdding: false })}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  {notesModal.notes.length === 0 ? (
                    <div className="text-muted">No notes available.</div>
                  ) : (
                    <ul className="list-group">
                      {notesModal.notes.map((noteObj, idx) => (
                        <li key={idx} className="list-group-item">
                          <div className='note-content'>
                            <pre>{noteObj.text}</pre>
                            <span className="text-muted small">{noteObj.username ? `${noteObj.username} - ` : ''}{noteObj.timestamp}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button className="btn btn-primary" onClick={async () => {
      if (notesModal.productId) await markNotesAsViewed(notesModal.productId);
      setNotesModal(prev => ({ ...prev, isAdding: true }));
    }}><i className="bi bi-plus me-1"></i>Add Note</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  )}
       
      </div>

      {/* Photo Modal */}
      {photoModal.show && (
        <>
          <div className="modal show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-images me-2"></i>
                    Product Photos ({photoModal.currentIndex + 1} of {photoModal.photos.length})
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setPhotoModal({ show: false, photos: [], currentIndex: 0 })}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  {photoModal.photos.length > 0 && (
                    <>
                      <img
                        src={photoModal.photos[photoModal.currentIndex]}
                        alt={`Photo ${photoModal.currentIndex + 1}`}
                        className="img-fluid"
                        onClick={() => window.open(photoModal.photos[photoModal.currentIndex], '_blank')}
                      />
                      {photoModal.photos.length > 1 && (
                        <div className="mt-3">
                          <button
                            className="btn btn-secondary me-2"
                            onClick={() => setPhotoModal(prev => ({
                              ...prev,
                              currentIndex: prev.currentIndex > 0 ? prev.currentIndex - 1 : prev.photos.length - 1
                            }))}
                          >
                            <i className="bi bi-chevron-left"></i> Previous
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => setPhotoModal(prev => ({
                              ...prev,
                              currentIndex: prev.currentIndex < prev.photos.length - 1 ? prev.currentIndex + 1 : 0
                            }))}
                          >
                            Next <i className="bi bi-chevron-right"></i>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div 
            className="modal-backdrop show"
            onClick={() => setPhotoModal({ show: false, photos: [], currentIndex: 0 })}
          ></div>
        </>
      )}

      {/* Follow Up Modal */}
      {followUpModal.show && (
        <>
         <div className="modal-backdrop show"  onClick={() => setNotesModal({ show: false, productId: null, notes: [], newNote: '', isAdding: false })}></div>
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-chat-text me-2"></i>
                  Follow-up Actions
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setFollowUpModal({
                    show: false,
                    productId: null,
                    followUps: [],
                    isAdding: false,
                    newNotes: '',
                    newPhotos: []
                  })}
                ></button>
              </div>
              <div className="modal-body">
                {/* Add New Follow Up */}
                {followUpModal.isAdding && (
                  <div className="followupmodal border rounded p-3 mb-3 ">
                    <h6>Add New Follow-up</h6>
                    <div className="mb-3">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={followUpModal.newNotes}
                        onChange={(e) => setFollowUpModal(prev => ({
                          ...prev,
                          newNotes: e.target.value
                        }))}
                        placeholder="Enter follow-up notes..."
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Photos</label>
                      <PhotoUploadComponent
                        onPhotosAdded={(photos) => setFollowUpModal(prev => ({
                          ...prev,
                          newPhotos: [...prev.newPhotos, ...photos]
                        }))}
                      />
                      {followUpModal.newPhotos.length > 0 && (
                        <div className="photo-preview-container mt-2">
                          {followUpModal.newPhotos.map((photo, index) => (
                            <div key={index} className="photo-preview">
                              <img src={photo} alt={`Preview ${index + 1}`} />
                              <button
                                type="button"
                                className="btn-remove-photo"
                                onClick={() => setFollowUpModal(prev => ({
                                  ...prev,
                                  newPhotos: prev.newPhotos.filter((_, i) => i !== index)
                                }))}
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-success"
                        onClick={addFollowUp}
                      >
                        <i className="bi bi-check me-1"></i>
                        Add Follow-up
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setFollowUpModal(prev => ({
                          ...prev,
                          isAdding: false,
                          newNotes: '',
                          newPhotos: []
                        }))}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  
                )}

                {/* Existing Follow Ups */}
                <div className="follow-up-timeline">
                  {followUpModal.followUps.length === 0 ? (
                    <div className="text-center py-3">
                      <i className="bi bi-chat-text display-4 text-muted"></i>
                      <p className="text-muted mt-2">No follow-ups yet</p>
                      {!followUpModal.isAdding && (
                        <button
                          className="btn btn-primary"
                          onClick={() => setFollowUpModal(prev => ({ ...prev, isAdding: true }))}
                        >
                          <i className="bi bi-plus me-1"></i>
                          Add First Follow-up
                        </button>
                      )}
                    </div>
                  ) : (
                    followUpModal.followUps.map((followUp) => (
                      <div key={followUp.id} className="follow-up-item">
                        <div className="follow-up-header">
                          <span className="follow-up-user">{followUp.user_name}</span>
                          <span className="follow-up-date">{formatDate(followUp.created_at)}</span>
                        </div>
                        <div className="follow-up-notes">{followUp.notes}</div>
                        {followUp.photos && followUp.photos.length > 0 && (
                          <div className="follow-up-photos">
                            {followUp.photos.map((photo, photoIndex) => (
                              <div key={photoIndex} className="follow-up-photo">
                                <img
                                  src={photo}
                                  alt={`Follow-up photo ${photoIndex + 1}`}
                                  onClick={() => window.open(photo, '_blank')}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="modal-footer">
                {!followUpModal.isAdding && followUpModal.followUps.length > 0 && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setFollowUpModal(prev => ({ ...prev, isAdding: true }))}
                  >
                    <i className="bi bi-plus me-1"></i>
                    Add Follow-up
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setFollowUpModal({
                    show: false,
                    productId: null,
                    followUps: [],
                    isAdding: false,
                    newNotes: '',
                    newPhotos: []
                  })}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}
       {/* Pagination */}
      </div>
      {/* Fixed Footer with Pagination */}
      {pendingProducts.length > 0 && (
        <div className="footer">
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between p-3 pagination-fixed" >
            <div className="text-muted">
              <span>
                Showing {((pagination.currentPage - 1) * pagination.recordsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords)} of {pagination.totalRecords} entries
              </span>
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = Math.max(1, pagination.currentPage - 2) + i;
                  if (page > pagination.totalPages) return null;
                  return (
                    <li key={page} className={`page-item ${pagination.currentPage === page ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default NewProducts;
